import React from 'react';
import { Reveal } from './WrapUp';
import type { Placement, WrapUpPhoto } from '../../utils/wrapUpPhotos';

/**
 * The report's photographs, drawn in whatever shape the page decided.
 *
 * Nothing here chooses anything. `placePhotos` in utils/wrapUpPhotos.ts
 * works out which seam gets which pictures and in what shape; this file
 * only knows how a plate, a pair, a trio and an inset are drawn. Keeping
 * the decision out of the component is what makes it possible to reason
 * about a report with three photographs and no testimonials without
 * rendering one.
 *
 * WHY THE CELLS CROP. A plate is a band across the page and the pairs and
 * trios are squares, so most frames lose something at the edges. That is
 * the right trade here and it is not the trade made elsewhere: a client's
 * FILM is shown in its own shape, always, because cropping a deliverable
 * tells the client something false about the work. These are decorative
 * frames carrying a document. The gallery does the same thing for the same
 * reason, and both show the picture whole the moment it is opened -- except
 * that these do not open at all, because they are illustration, not
 * delivery. The full set is one row in the downloads at the end.
 *
 * They are not clickable for that reason. A picture that does nothing when
 * you press it is better than a lightbox that competes with the report.
 */

function Frame({
  photo,
  aspect
}: {
  photo: WrapUpPhoto;
  aspect: string;
}) {
  return <span className={`relative block w-full overflow-hidden bg-paperDeep ${aspect}`} data-id="element-311" data-mp="3j6yqby3tz3w1">
      <img src={photo.url} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" data-id="element-312" data-mp="3j4vtau49gaht" />
    </span>;
}

/**
 * One seam's worth of photographs. Returns null for the inset, which is
 * drawn inside the ink block by WrapUpClosing rather than on the paper.
 */
export function PhotoBand({
  placement
}: {
  placement: Placement | undefined;
}) {
  if (!placement || placement.photos.length === 0) return null;
  if (placement.shape === 'inset') return null;
  if (placement.shape === 'plate') {
    return <Reveal data-id="element-313" data-mp="11xm1vmi6linx">
        {/* Taller on a phone: a 16:7 band at 414px is a letterbox slit. */}
        <Frame photo={placement.photos[0]} aspect="aspect-[3/2] sm:aspect-[16/7]" data-id="element-314" data-mp="x0n66zivd3nm" />
      </Reveal>;
  }
  if (placement.shape === 'pair') {
    return <div className="grid grid-cols-2 gap-2 sm:gap-5" data-id="element-315" data-mp="1po9jx0lt9oo7">
        {placement.photos.map((photo, index) => <Reveal key={photo.url} delay={index * 0.06} data-id="element-316" data-mp="1kpesja9s2rsh" data-mpi={index}>
            <Frame photo={photo} aspect="aspect-square" data-id="element-317" data-mp="1kpyaus3cbi71" />
          </Reveal>)}
      </div>;
  }

  /**
   * A trio. THREE ACROSS A PHONE IS A POSTAGE STAMP -- measured at 117px a
   * side on a 414px screen, which is smaller than the type beside it and
   * makes a photograph look like an icon. So the phone takes two and lets
   * the third run the full width as a band, exactly the way the figures
   * band handles an odd last card. Three across from `sm` up, where the
   * cells are 353px and the row reads as a strip of frames.
   */
  return <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-5" data-id="element-318" data-mp="2fxgpocor5s12">
      {placement.photos.map((photo, index) => {
      const runsOn = index === 2;
      return <Reveal key={photo.url} delay={index * 0.06} className={runsOn ? 'col-span-2 sm:col-span-1' : ''} data-id="element-319" data-mp="2pwg0o1wzpzr8" data-mpi={index}>
            <Frame photo={photo} aspect={runsOn ? 'aspect-[3/2] sm:aspect-square' : 'aspect-square'} data-id="element-320" data-mp="2txi0okyqefu9" />
          </Reveal>;
    })}
    </div>;
}

/**
 * The frame that sits inside the closing ink block, beside the paragraph.
 *
 * This is the seam worth having. The ink block is the darkest and emptiest
 * moment on the page -- a 48ch paragraph in a full-width black field -- and
 * a standing figure beside it is the most designed thing in the report. The
 * allocator asks for a portrait here for that reason, and takes a landscape
 * only when no portrait was uploaded.
 */
export function PhotoInset({
  photo
}: {
  photo: WrapUpPhoto;
}) {
  return <div className="mt-10 sm:mt-0 sm:w-[38%] sm:shrink-0" data-id="element-321" data-mp="e7hvlzgw9omw">
      <Frame photo={photo} aspect="aspect-[4/3] sm:aspect-[3/4]" data-id="element-322" data-mp="rzxs0hfaioeu" />
    </div>;
}